/**
 * Client-side PDF generator for cook plans
 * Uses jsPDF to create downloadable reports
 */

import { jsPDF } from 'jspdf';
import type { CookDetails, CookEstimate, WeatherData, SmokerSpec, MeatProperties } from '../types';
import { format } from 'date-fns';

export class PDFGenerator {
  private doc: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private y: number;
  private logoDataUrl: string | null = null;
  private logoAspectRatio: number | null = null;

  constructor() {
    this.doc = new jsPDF();
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 20;
    this.y = this.margin;
  }

  /**
  * Load BBQLabs.ai logo for PDF header
   */
  private async loadLogo(): Promise<void> {
    try {
      const response = await fetch('/bbqlabs-full-mark.png');
      const blob = await response.blob();
      const reader = new FileReader();
      await new Promise<void>((resolve, reject) => {
        reader.onloadend = () => {
          this.logoDataUrl = reader.result as string;
          resolve();
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const image = new Image();
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = reject;
        image.src = this.logoDataUrl || '';
      });
      this.logoAspectRatio = image.naturalWidth / image.naturalHeight;
    } catch (error) {
      console.warn('Failed to load logo for PDF:', error);
      this.logoDataUrl = null;
      this.logoAspectRatio = null;
    }
  }

  /**
   * Generate cook plan PDF
   */
  async generate(
    cookDetails: CookDetails,
    estimate: CookEstimate,
    weather: WeatherData,
    smokerSpec: SmokerSpec,
    meatProps: MeatProperties
  ): Promise<void> {
    // Load logo first
    await this.loadLogo();
    // Header
    this.addHeader();
    
    // Cook Summary
    this.addSection('Cook Summary');
    this.addKeyValue('Smoker', smokerSpec.fullName);
    this.addKeyValue('Meat', meatProps.displayName);
    if (cookDetails.variant) {
      const variant = meatProps.variants?.[cookDetails.variant];
      if (variant) {
        this.addKeyValue('Variant', variant.displayName);
      }
    }
    this.addKeyValue('Number of Pieces', cookDetails.pieces.length.toString());
    const totalWeight = cookDetails.pieces.reduce((sum, p) => sum + p.weight, 0);
    this.addKeyValue('Total Weight', `${totalWeight.toFixed(1)} lbs`);
    this.addKeyValue('Smoker Temperature', `${cookDetails.smokerTempF}°F`);
    this.addKeyValue('Target Temperature', `${cookDetails.targetTempF}°F`);
    
    this.y += 5;
    
    // Timeline
    this.addSection('Timeline');
    if (cookDetails.targetServingTime) {
      this.addKeyValue('Target Serving Time', format(new Date(cookDetails.targetServingTime), 'EEE, MMM d, yyyy h:mm a'));
    }
    this.addKeyValue('Total Cook Time', `${estimate.summary.totalCookTimeHours.toFixed(1)} hours`);
    this.addKeyValue('Total with Rest', `${estimate.summary.totalTimeIncludingRestHours.toFixed(1)} hours`);
    this.addKeyValue('Recommended Start Time', format(estimate.timeline.startTime, 'EEE, MMM d, yyyy h:mm a'));
    this.addKeyValue('Expected Finish Time', format(estimate.timeline.endTime, 'EEE, MMM d, yyyy h:mm a'));
    this.addKeyValue('Confidence', `${estimate.summary.confidenceLevel} (±${estimate.summary.uncertaintyRangeHours}h)`);
    if (cookDetails.targetServingTime) {
      this.addText('Note: Timeline calculated working backward from your target serving time.', 8);
    }
    
    this.y += 5;

    // Weather Analysis (moved before Phase Predictions to match UI order)
    this.addSection('Weather Analysis');
    this.addKeyValue('Location', weather.location.name);
    
    // Show forecast analysis if available, otherwise current conditions
    if (weather.hourly && weather.hourly.length > 0 && estimate.timeline?.startTime && estimate.timeline?.endTime) {
      const startTime = new Date(estimate.timeline.startTime);
      const endTime = new Date(estimate.timeline.endTime);
      
      // Filter forecast to cook window
      const cookWindowForecast = weather.hourly.filter(hour => {
        const hourTime = new Date(hour.time);
        return hourTime >= startTime && hourTime <= endTime;
      });
      
      if (cookWindowForecast.length > 0) {
        const temps = cookWindowForecast.map(h => h.temperatureF);
        const winds = cookWindowForecast.map(h => h.windMph);
        const avgTemp = temps.reduce((sum, t) => sum + t, 0) / temps.length;
        const minTemp = Math.min(...temps);
        const maxTemp = Math.max(...temps);
        const maxWind = Math.max(...winds);
        const precipHours = cookWindowForecast.filter(h => h.precipitation).length;
        const precipProbability = (precipHours / cookWindowForecast.length) * 100;
        
        this.addText(`Forecast During Your Cook Window (${cookWindowForecast.length} hours)`, 5);
        this.addKeyValue('Temperature Range', `${minTemp.toFixed(0)}°F - ${maxTemp.toFixed(0)}°F`);
        this.addKeyValue('Average Temperature', `${avgTemp.toFixed(0)}°F`);
        this.addKeyValue('Max Wind Speed', `${maxWind.toFixed(0)} mph`);
        this.addKeyValue('Precipitation Probability', `${precipProbability.toFixed(0)}%`);
      } else {
        // Forecast available but not covering cook window - show current
        this.addText('Using current conditions (forecast not covering cook window)', 5);
        this.addKeyValue('Temperature', `${weather.current.temperatureF.toFixed(0)}°F (feels like ${weather.current.feelsLikeF.toFixed(0)}°F)`);
        this.addKeyValue('Wind Speed', `${weather.current.windMph.toFixed(0)} mph`);
        this.addKeyValue('Precipitation', weather.current.precipitation ? 'Yes' : 'No');
      }
    } else {
      // No forecast data - show current conditions
      this.addText('Using current conditions only', 5);
      this.addKeyValue('Temperature', `${weather.current.temperatureF.toFixed(0)}°F (feels like ${weather.current.feelsLikeF.toFixed(0)}°F)`);
      this.addKeyValue('Wind Speed', `${weather.current.windMph.toFixed(0)} mph`);
      this.addKeyValue('Precipitation', weather.current.precipitation ? 'Yes' : 'No');
    }
    
    // Check if we need a new page
    if (this.y > this.pageHeight - 80) {
      this.doc.addPage();
      this.y = this.margin;
    }
    
    this.y += 5;

    // Phase Predictions (after Weather Analysis to match UI order)
    this.addSection('Phase Predictions');
    this.addKeyValue('Heat-up Phase', `${estimate.phases.heatUp.durationHours.toFixed(1)} hours`);
    this.addText(estimate.phases.heatUp.explanation, 8);
    if (estimate.phases.stall.durationHours > 0) {
      this.addKeyValue('Stall Phase', `${estimate.phases.stall.durationHours.toFixed(1)} hours`);
      this.addText(estimate.phases.stall.explanation, 8);
    }
    this.addKeyValue('Post-stall/Finishing', `${estimate.phases.postStall.durationHours.toFixed(1)} hours`);
    this.addText(estimate.phases.postStall.explanation, 8);
    this.addKeyValue('Rest Time', `${estimate.phases.rest.durationHours.toFixed(1)} hours`);
    this.addText(estimate.phases.rest.explanation, 8);
    
    this.y += 5;
    
    // Cook Time Breakdown Summary
    this.addSection('Cook Time Breakdown');
    this.addKeyValue('Baseline Cook Time', `${estimate.summary.baselineHours.toFixed(1)} hours`);
    this.addKeyValue('Weather Adjustment', `${estimate.summary.weatherAdjustmentHours > 0 ? '+' : ''}${estimate.summary.weatherAdjustmentHours.toFixed(1)} hours`);
    this.addKeyValue('Strategy Adjustment', `${estimate.summary.strategyAdjustmentHours > 0 ? '+' : ''}${estimate.summary.strategyAdjustmentHours.toFixed(1)} hours`);
    this.addKeyValue('Total Cook Time', `${estimate.summary.totalCookTimeHours.toFixed(1)} hours`);
    
    this.y += 5;
    
    // Impact Factors
    this.addSection('Impact Factors');
    this.addKeyValue('Smoker Efficiency', `${(estimate.factors.smokerEfficiency * 100).toFixed(0)}%`);
    this.addKeyValue('Weather Impact', `${estimate.factors.weatherImpact.toFixed(3)}x`);
    this.addKeyValue('Wind Impact', `${estimate.factors.windImpact.toFixed(3)}x`);
    this.addKeyValue('Precipitation Impact', `${estimate.factors.precipitationImpact.toFixed(3)}x`);
    this.addKeyValue('Spacing Factor', `${estimate.factors.spacingFactor.toFixed(2)}x`);
    this.addKeyValue('Thermal Load Factor', `${estimate.factors.thermalLoadFactor.toFixed(3)}x`);
    
    // Check for page break
    if (this.y > this.pageHeight - 100) {
      this.doc.addPage();
      this.y = this.margin;
    }
    
    this.y += 5;
    
    // Ready Schedule (moved before Holding Window to match UI order)
    if (estimate.perPiece.length > 1) {
      this.addSection('Ready Schedule');
      
      // Current Plan
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'bold');
      this.addText('Your Current Plan:', 5);
      this.doc.setFont('helvetica', 'normal');
      this.addText(`Based on ${estimate.perPiece[0]?.restHours || 0}h rest for all pieces`, 8);
      this.y += 3;
      
      estimate.perPiece.forEach(piece => {
        const marker = piece.finishesBeforeLargest ? '[Early]' : '[Ready]';
        const line = `${marker} Piece ${piece.id} (${piece.weight} lbs): Ready at ${format(piece.readyTime, 'h:mm a')}`;
        const details = ` [Cook ${piece.cookTimeHours.toFixed(1)}h + Rest ${piece.restHours}h]`;
        const holdText = piece.holdHours > 0 ? ` -> Hold ${piece.holdHours.toFixed(1)}h${piece.holdHours > 2 ? ' [!]' : ''}` : '';
        this.addText(line + details + holdText, 10);
      });
      
      // Safety warning for current plan
      if (estimate.perPiece.some(p => p.holdHours > 2)) {
        this.y += 3;
        this.addText('WARNING: Some pieces require >2h warm hold. See recommended schedule below.', 8);
      }
      
      // Recommended Schedule
      if (estimate.recommendedSchedule && estimate.recommendedSchedule.some(p => p.adjustmentReason)) {
        this.y += 5;
        this.doc.setFontSize(10);
        this.doc.setFont('helvetica', 'bold');
        this.addText('Recommended Schedule:', 5);
        this.doc.setFont('helvetica', 'normal');
        this.addText('Adjusted for food safety (max 2h warm hold, max 2.5h rest)', 8);
        this.y += 3;
        
        estimate.recommendedSchedule.forEach(piece => {
          const marker = piece.finishesBeforeLargest ? '[Early]' : '[Ready]';
          const line = `${marker} Piece ${piece.id} (${piece.weight} lbs): Ready at ${format(piece.readyTime, 'h:mm a')}`;
          const details = ` [Cook ${piece.cookTimeHours.toFixed(1)}h + Rest ${piece.restHours}h]`;
          const holdText = piece.holdHours > 0 ? ` -> Hold ${piece.holdHours.toFixed(1)}h${piece.holdHours > 2 ? ' [!]' : ''}` : '';
          this.addText(line + details + holdText, 10);
          
          if (piece.adjustmentReason) {
            this.addText(`   NOTE: ${piece.adjustmentReason}`, 8);
          }
        });
      }
      
      this.y += 3;
      this.addText('[Early] = Ready early, needs extended rest or warm hold | [Ready] = Largest piece, sets timeline', 8);
      this.y += 5;
    }

    // Check for page break before Holding Window
    if (this.y > this.pageHeight - 80) {
      this.doc.addPage();
      this.y = this.margin;
    }

    // Holding Window (moved after Ready Schedule to match UI order)
    if (estimate.holdingWindow) {
      this.addSection('Flexible Start Window');
      this.addKeyValue('Earliest Safe Start', format(new Date(estimate.holdingWindow.earliestSafeStart), 'EEE h:mm a'));
      this.addKeyValue('Recommended Start', format(new Date(estimate.holdingWindow.recommendedStart), 'EEE h:mm a'));
      this.addKeyValue('Latest Safe Start', format(new Date(estimate.holdingWindow.latestSafeStart), 'EEE h:mm a'));
      this.addKeyValue('Total Flexibility', `${estimate.holdingWindow.totalFlexibilityHours} hours`);
      this.addText(estimate.holdingWindow.explanation, 8);
      this.y += 5;
    }
    
    // Warnings
    if (estimate.warnings.length > 0) {
      // Check for page break
      if (this.y > this.pageHeight - 60) {
        this.doc.addPage();
        this.y = this.margin;
      }
      
      this.addSection('*** WARNINGS ***');
      estimate.warnings.forEach(warning => {
        // Strip emojis and Unicode symbols for PDF compatibility
        const cleanWarning = warning.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{231A}-\u{231B}\u{23E9}-\u{23FA}\u{25AA}-\u{25C0}\u{25FB}-\u{25FE}\u{2934}-\u{2935}\u{2B05}-\u{2B07}\u{2B1B}-\u{2B1C}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F170}-\u{1F171}\u{1F17E}-\u{1F17F}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}]/gu, '');
        this.addText(cleanWarning.trim(), 10);
      });
      
      this.y += 5;
    }
    
    // Recommendations
    if (estimate.recommendations.length > 0) {
      // Check for page break
      if (this.y > this.pageHeight - 60) {
        this.doc.addPage();
        this.y = this.margin;
      }
      
      this.addSection('RECOMMENDATIONS');
      estimate.recommendations.forEach(rec => {
        // Strip emojis and Unicode symbols for PDF compatibility, add ASCII bullet
        const cleanRec = rec.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{231A}-\u{231B}\u{23E9}-\u{23FA}\u{25AA}-\u{25C0}\u{25FB}-\u{25FE}\u{2934}-\u{2935}\u{2B05}-\u{2B07}\u{2B1B}-\u{2B1C}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F170}-\u{1F171}\u{1F17E}-\u{1F17F}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}]/gu, '');
        this.addText(`- ${cleanRec.trim()}`, 10);
      });
      
      this.y += 5;
    }

    // Cook Strategy
    this.addSection('Cook Strategy');
    
    // Spacing (if multi-piece)
    if (cookDetails.pieces.filter(p => p.weight > 0).length > 1) {
      const spacingText = cookDetails.spacing?.arrangement 
        ? `${cookDetails.spacing.arrangement} (${cookDetails.spacing.spacingFactor}x)` 
        : 'Not specified (assumed gaps)';
      this.addKeyValue('Piece Arrangement', spacingText);
    }
    
    const wrapText = cookDetails.wrapping?.enabled 
      ? `Yes, ${cookDetails.wrapping.wrapType === 'butcher-paper' ? 'butcher paper' : 'foil'} at ${cookDetails.wrapping.atTempF}°F` 
      : 'No (naked cook)';
    this.addKeyValue('Wrapping', wrapText);
    
    const spritzText = cookDetails.spritz?.enabled 
      ? `${cookDetails.spritz.totalOpenings || 0} lid openings total` 
      : 'No';
    this.addKeyValue('Spritzing/Mopping', spritzText);
    
    this.addKeyValue('Weather Exposure', this.getExposureText(cookDetails.exposure));
    
    // Check if we need a new page
    if (this.y > this.pageHeight - 60) {
      this.doc.addPage();
      this.y = this.margin;
    }

    this.y += 5;

    // Calculation Metadata
    this.addSection('Calculation Metadata');
    this.addKeyValue('Timestamp', format(new Date(estimate.calculation.timestamp), 'MMM d, yyyy h:mm:ss a'));
    this.addKeyValue('Input Hash', estimate.calculation.inputHash);
    this.addKeyValue('Calculator Version', estimate.calculation.version);

    // Footer
    this.addFooter();
  }

  /**
   * Save PDF to file
   */
  save(filename: string): void {
    this.doc.save(filename);
  }

  /**
   * Get PDF as blob
   */
  getBlob(): Blob {
    return this.doc.output('blob');
  }

  // Private helper methods

  private addHeader(): void {
    // Add BBQLabs.ai logo if loaded
    if (this.logoDataUrl) {
      try {
        const maxLogoWidth = 150;
        const maxLogoHeight = 38;
        const aspectRatio = this.logoAspectRatio || 3.5;
        const logoWidth = Math.min(maxLogoWidth, maxLogoHeight * aspectRatio);
        const logoHeight = logoWidth / aspectRatio;
        this.doc.addImage(this.logoDataUrl, 'PNG', this.margin, this.y - 2, logoWidth, logoHeight);
        this.y += logoHeight + 6;
      } catch (error) {
        console.warn('Failed to add logo to PDF:', error);
        // Fall back to text title
        this.doc.setFontSize(24);
        this.doc.setTextColor(220, 38, 38);
        this.doc.text('BBQ Cook Plan', this.margin, this.y);
        this.y += 10;
      }
    } else {
      // No logo - use text title
      this.doc.setFontSize(24);
      this.doc.setTextColor(220, 38, 38);
      this.doc.text('BBQ Cook Plan', this.margin, this.y);
      this.y += 10;
    }
    
    this.doc.setFontSize(10);
    this.doc.setTextColor(107, 114, 128); // Smoke gray
    this.doc.text(`Generated: ${format(new Date(), 'EEE, MMM d, yyyy h:mm a')}`, this.margin, this.y);
    
    this.y += 10;
    this.doc.setDrawColor(229, 231, 235);
    this.doc.line(this.margin, this.y, this.pageWidth - this.margin, this.y);
    this.y += 10;
  }

  private addSection(title: string): void {
    this.doc.setFontSize(14);
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(title, this.margin, this.y);
    this.y += 8;
    this.doc.setFont('helvetica', 'normal');
  }

  private addKeyValue(key: string, value: string): void {
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99);
    this.doc.text(key + ':', this.margin + 5, this.y);
    
    // Wrap value text if too long
    const maxWidth = this.pageWidth - this.margin * 2 - 65;
    const lines = this.doc.splitTextToSize(value, maxWidth);
    
    this.doc.setTextColor(31, 41, 55);
    this.doc.setFont('helvetica', 'bold');
    
    lines.forEach((line: string, index: number) => {
      this.doc.text(line, this.margin + 60, this.y);
      if (index < lines.length - 1) {
        this.y += 5;
      }
    });
    
    this.doc.setFont('helvetica', 'normal');
    this.y += 6;
  }

  private addText(text: string, indent: number = 5): void {
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99);
    
    // Wrap text if too long
    const maxWidth = this.pageWidth - this.margin * 2 - indent;
    const lines = this.doc.splitTextToSize(text, maxWidth);
    
    lines.forEach((line: string, index: number) => {
      this.doc.text(line, this.margin + indent, this.y);
      if (index < lines.length - 1) {
        this.y += 5;
      }
    });
    
    this.y += 6;
  }

  // Keeping for potential future use
  // @ts-expect-error - Keeping for potential future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private _addBullet(text: string): void {
    this.doc.setFontSize(10);
    this.doc.setTextColor(75, 85, 99);
    this.doc.text('•', this.margin + 5, this.y);
    
    // Wrap text if too long
    const maxWidth = this.pageWidth - this.margin * 2 - 10;
    const lines = this.doc.splitTextToSize(text, maxWidth);
    
    lines.forEach((line: string, index: number) => {
      this.doc.text(line, this.margin + 10, this.y);
      if (index < lines.length - 1) {
        this.y += 5;
      }
    });
    
    this.y += 6;
  }

  private addFooter(): void {
    const footerY = this.pageHeight - 10;
    
    // App info with URL
    this.doc.setFontSize(8);
    this.doc.setTextColor(156, 163, 175);
    this.doc.text(
      'Precision planning powered by thermodynamics and real-time weather data',
      this.pageWidth / 2,
      footerY,
      { align: 'center' }
    );
  }

  private getExposureText(exposure: { useWeatherTemp: boolean; useWeatherWind: boolean; useWeatherPrecipitation: boolean }): string {
    if (exposure.useWeatherTemp && exposure.useWeatherWind && exposure.useWeatherPrecipitation) {
      return 'Fully exposed';
    }
    const parts = [];
    if (!exposure.useWeatherTemp) parts.push('temp-controlled');
    if (!exposure.useWeatherWind) parts.push('wind-protected');
    if (!exposure.useWeatherPrecipitation) parts.push('rain-protected');
    return parts.length > 0 ? parts.join(', ') : 'Partially protected';
  }
}
